import { Modifier } from '../modifier';
import type { Damage } from '../damage.type';
import { addModifier, dealDamage, healDamage } from '../player.type';
import type { Technique } from '../techniques.type';
import { techniqueHistory } from '../../../store';
import type { Die } from '../die.type';
import type { Player } from '../player.type';
import {
  ModifierPriority,
  ModifierTags,
  StatusEffect,
  type ModifierParams,
} from '../modifier.type';
import { SoundType } from '../../components/audio.svelte';
import { StatusEffectRegistry } from '../status-effects';

export class Haste extends Modifier {
  static readonly baseTitle = 'Haste';
  static readonly baseText =
    'Gain access to haste techniques, "Follow Up", "Buckle Up", "Follow Through". Stable.';
  static readonly baseIcon = 'agility';
  static readonly baseTags = [ModifierTags.Boon];
  static readonly basePriority = ModifierPriority.Middle;

  // Instance properties that can change
  title = Haste.baseTitle;
  text = Haste.baseText;
  icon = Haste.baseIcon;
  tags: Array<ModifierTags> = [...Haste.baseTags];
  priority = Haste.basePriority;

  getTechniques(): Technique[] {
    return [
      {
        title: 'Follow Up',
        text: 'Deal 1 Damage.',
        tags: ['chain', 'damage'],
        ingredients: [],
        requirements: [
          {
            text: 'Previous technique was an attack',
            icon: 'fist',
            test: (source: Player, target: Player) => {
              const lastAction = techniqueHistory[0];
              return (
                (lastAction?.tags?.includes('damage') && !lastAction?.tags?.includes('chain')) ||
                false
              );
            },
          },
        ],
        audio: SoundType.AttackBlunt,
        activate: (target: Player, source: Player, dice: Array<Die>) => {
          dealDamage({ value: 1, target, source, dice });
        },
      },
      {
        title: 'Buckle Up',
        text: 'Gain 1 Block.',
        tags: ['chain', 'defence'],
        ingredients: [],
        requirements: [
          {
            text: 'Previous technique was a defence',
            icon: 'bone',
            test: (source: Player, target: Player) => {
              const lastAction = techniqueHistory[0];
              return (
                (lastAction?.tags?.includes('defence') && !lastAction?.tags?.includes('chain')) ||
                false
              );
            },
          },
        ],
        audio: SoundType.BuffArmour,
        activate: (target: Player, source: Player, dice: Array<Die>) => {
          addModifier(source, StatusEffect.Block, 1);
        },
      },
      {
        title: 'Follow Through',
        text: 'Heal 1 Health.',
        tags: ['chain', 'heal'],
        ingredients: [],
        requirements: [
          {
            text: 'Previous technique was a heal',
            icon: 'regeneration',
            test: (source: Player, target: Player) => {
              const lastAction = techniqueHistory[0];
              return (
                (lastAction?.tags?.includes('heal') && !lastAction?.tags?.includes('chain')) ||
                false
              );
            },
          },
        ],
        audio: SoundType.Heal,
        activate: (target: Player, source: Player, dice: Array<Die>) => {
          healDamage({ value: 1, target: source, source, dice });
        },
      },
    ];
  }

  update(stacks: number, params?: ModifierParams) {
    super.update(stacks, params);
    this.delay = true;
  }

  startTurn() {
    if (this.delay) {
      const focus = this.owner.statusEffects.find(
        (modifier) => modifier.type === StatusEffect.Focus,
      );
      if (!focus) {
        this.delay = false;
      }
    }
  }

  endTurn() {
    if (!this.delay) {
      this.removeStacks(1);
    }
  }
}

// Register the status effect
StatusEffectRegistry.register(StatusEffect.Haste, Haste);
